import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { TooltipModule } from 'primeng/tooltip';
import { AvatarModule } from 'primeng/avatar';
import { CardModule } from 'primeng/card';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ApiService } from '@/services/api.service';
import { AuthService } from '@/services/auth.service';
import { ToastService } from '@/services/toast.service';
import { AvailableFilterDto } from '@/helper/dto/available-filter.dto';
import { SupportedOperationsDto } from '@/helper/dto/supported-operations.dto';
import { UserDto } from '@/helper/dto/user.dto';
import { debounceTime, distinctUntilChanged, Subject, Subscription, firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-impersonate-user-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    ButtonModule,
    InputTextModule,
    IconFieldModule,
    InputIconModule,
    TooltipModule,
    AvatarModule,
    CardModule,
    ProgressSpinnerModule
  ],
  templateUrl: './impersonate-user-dialog.component.html',
  styleUrl: './impersonate-user-dialog.component.scss'
})
export class ImpersonateUserDialogComponent implements OnInit, OnDestroy {
  selectedUser: UserDto | null = null;
  searchQuery = '';
  loading = false;
  recentImpersonations: UserDto[] = [];
  searchResults: UserDto[] = [];

  private searchSubject = new Subject<string>();
  private searchSubscription?: Subscription;

  constructor(
    public ref: DynamicDialogRef,
    private apiService: ApiService,
    private authService: AuthService,
    private toastService: ToastService,
    private translateService: TranslateService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadRecentImpersonations();

    this.searchSubscription = this.searchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged()
      )
      .subscribe((query) => {
        if (query.trim().length > 0) {
          this.searchUsers(query);
        }
      });
  }

  ngOnDestroy(): void {
    this.searchSubscription?.unsubscribe();
  }

  onSearchChange(): void {
    this.searchSubject.next(this.searchQuery);
  }

  private async searchUsers(query: string): Promise<void> {
    this.loading = true;
    const filters: AvailableFilterDto[] = [
      {
        key: 'isSystemAdmin',
        displayName: 'Administrator',
        supportedOperations: [SupportedOperationsDto.EQUALS],
        selectedOperation: SupportedOperationsDto.EQUALS,
        valueType: 'boolean',
        value: false,
        isInternalFilter: false
      }
    ];

    try {
      const result = await firstValueFrom(this.apiService.searchUsers({ filters, search: query, size: 20 }));
      this.searchResults = result.data || [];
    } catch (error) {
      console.error('Error searching users:', error);
      this.searchResults = [];
    } finally {
      this.loading = false;
    }
  }

  selectUser(user: UserDto): void {
    this.selectedUser = user;
  }

  loadRecentImpersonations(): void {
    const stored = localStorage.getItem('efskarriere-recent-impersonations');
    if (stored) {
      try {
        this.recentImpersonations = JSON.parse(stored);
      } catch (e) {
        this.recentImpersonations = [];
      }
    }

    this.backfillOidcIdentifiers();
  }

  private async backfillOidcIdentifiers(): Promise<void> {
    const incomplete = this.recentImpersonations.filter(u => u._id && !u.oidcIdentifier);
    if (incomplete.length === 0) {
      return;
    }

    await Promise.all(
      incomplete.map(async (user) => {
        try {
          const fetched = await firstValueFrom(this.apiService.getUserById(user._id!));
          user.oidcIdentifier = fetched.oidcIdentifier;
        } catch (error) {
          console.error('Error loading oidcIdentifier for recent impersonation:', error);
        }
      })
    );

    localStorage.setItem('efskarriere-recent-impersonations', JSON.stringify(this.recentImpersonations));
  }

  selectRecentImpersonation(user: UserDto): void {
    this.selectedUser = user;
  }

  clearSelection(): void {
    this.selectedUser = null;
  }

  async impersonate(): Promise<void> {
    if (!this.selectedUser) {
      await this.toastService.showErrorToast(
        this.translateService.instant('generics.error'),
        this.translateService.instant('impersonate.select_user')
      );
      return;
    }

    try {
      await this.authService.impersonateUser(this.selectedUser._id!);
      this.addToRecentImpersonations(this.selectedUser);
      await this.toastService.showSuccessToast(
        this.translateService.instant('generics.success'),
        this.translateService.instant('impersonate.success')
      );
      this.ref.close(this.selectedUser);

      // Check if currently on home page
      const isOnHomePage = window.location.pathname.includes('/home');

      if (isOnHomePage) {
        // Reload page to refresh data when impersonating from home
        window.location.href = '/home';
      } else {
        // Navigate to home from admin
        this.router.navigate(['/home']);
      }
    } catch (error) {
      console.error('Impersonation error:', error);
      await this.toastService.showErrorToast(
        this.translateService.instant('generics.error'),
        this.translateService.instant('impersonate.failed')
      );
    }
  }

  private addToRecentImpersonations(user: UserDto): void {
    const index = this.recentImpersonations.findIndex(u => u._id === user._id);
    if (index > -1) {
      this.recentImpersonations.splice(index, 1);
    }

    this.recentImpersonations.unshift({
      _id: user._id,
      firstname: user.firstname,
      lastname: user.lastname,
      mail: user.mail,
      oidcIdentifier: user.oidcIdentifier,
      profilePictureBase64: user.profilePictureBase64
    } as UserDto);

    this.recentImpersonations = this.recentImpersonations.slice(0, 5);
    localStorage.setItem('efskarriere-recent-impersonations', JSON.stringify(this.recentImpersonations));
  }

  cancel(): void {
    this.ref.close();
  }
}
